import type { UseStore } from 'zustand';
import shallow from 'zustand/shallow';
import { useEffect, useMemo } from 'react';
import type { EhsFormioSVSSelectStore } from './EhsFormioSVSSelect';
import type { infrastructurePortalUtil } from 'generated-ts-common';
import { createOtherOption } from './FormioSVSUtil';

type GenCode = infrastructurePortalUtil.saf.infrastructure.model.svs.GenCode;

type UseLoadSVSSelectStoreDataParams = {
  valueSetOid: string;
  includeOther?: boolean;
  preloadedItems?: GenCode[];
  store?: UseStore<EhsFormioSVSSelectStore>;
};

type SVSStoreSlice = Pick<
  EhsFormioSVSSelectStore,
  'data' | 'load' | 'isLoading'
>;

const emptyStoreData: Record<string, GenCode[]> = {};
const emptyLoad: SVSStoreSlice['load'] = async () => {};
const emptyIsLoading: SVSStoreSlice['isLoading'] = () => false;

export function useLoadSVSSelectStoreData({
  valueSetOid,
  includeOther = false,
  preloadedItems,
  store
}: UseLoadSVSSelectStoreDataParams) {
  const [storeData, loadSVSValues, isLoading]: [
    Record<string, GenCode[]>,
    SVSStoreSlice['load'],
    SVSStoreSlice['isLoading']
  ] = store?.(
    (state: SVSStoreSlice) => [state.data, state.load, state.isLoading],
    shallow
  ) ?? [emptyStoreData, emptyLoad, emptyIsLoading]; // empty state when the store is not set yet

  // automatically load the items, if the url is initialized properly
  useEffect(() => {
    if (preloadedItems != null) {
      return;
    }

    if (!storeData[valueSetOid]) {
      loadSVSValues(valueSetOid);
    }
  }, [preloadedItems, storeData, loadSVSValues, valueSetOid]);

  const items: Array<GenCode> = useMemo(
    () => preloadedItems ?? storeData[valueSetOid] ?? [],
    [preloadedItems, valueSetOid, storeData]
  );

  const selectItems: Array<GenCode> = useMemo(() => {
    return includeOther ? [...items, createOtherOption()] : items;
  }, [items, includeOther]);

  return { storeData, items, selectItems, isLoading };
}
